import { Button } from "@/components/ui/button";
import { Globe, X, ExternalLink } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";

const SourcesSidebar = memo(function SourcesSidebar({
  isOpen,
  onClose,
  sources = [],
  title = "Citations",
}) {
  // Listen for global sidebar close events
  useEffect(() => {
    const handleCloseAllSidebars = (event) => {
      if (isOpen && onClose) {
        onClose();
      }
    };

    window.addEventListener("close-all-sidebars", handleCloseAllSidebars);

    return () => {
      window.removeEventListener("close-all-sidebars", handleCloseAllSidebars);
    };
  }, [isOpen, onClose]);

  // Parse sources data if it's in string format
  const parsedSources = useMemo(() => {
    if (!sources) return [];

    // Handle different data structures
    let sourceData = sources;

    // If sources is a nested object, extract the actual data
    if (sources.result?.data) {
      sourceData = sources.result.data;
    } else if (sources.data) {
      sourceData = sources.data;
    } else if (sources.result?.result?.data) {
      sourceData = sources.result.result.data;
    }

    // For the new web search format, filter out the main content (first item with no URL)
    // and only keep the source citations
    if (Array.isArray(sourceData)) {
      sourceData = sourceData.filter((item) => item.url && item.url !== null);
    }

    // If sourceData is a string, parse it
    if (typeof sourceData === "string") {
      const results = [];
      const entries = sourceData
        .split("\n\n\n")
        .filter((entry) => entry.trim());

      for (const entry of entries) {
        const lines = entry.split("\n").filter((line) => line.trim());

        let url = "";
        let title = "";
        let description = "";

        for (const line of lines) {
          if (line.startsWith("URL: ")) {
            url = line.replace("URL: ", "").trim();
          } else if (line.startsWith("Title: ")) {
            title = line.replace("Title: ", "").trim();
          } else if (line.startsWith("Description: ")) {
            description = line.replace("Description: ", "").trim();
          }
        }

        if (url && title) {
          results.push({
            url,
            title,
            description,
            favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`,
          });
        }
      }

      return results;
    }

    // If sourceData is already an array, return it
    if (Array.isArray(sourceData)) {
      return sourceData.map((source) => {
        let favicon = source.favicon;
        if (!favicon && source.url) {
          try {
            favicon = `https://www.google.com/s2/favicons?domain=${new URL(source.url).hostname}&sz=32`;
          } catch (error) {
            console.warn("Invalid URL for favicon generation:", source.url);
            favicon = null;
          }
        }
        return {
          ...source,
          favicon,
        };
      });
    }

    return [];
  }, [sources]);

  // Show first 10 sources with expandable "show more" (matching DeepResearchBlock)
  const [showAllSources, setShowAllSources] = useState(false);
  const visibleSources = showAllSources
    ? parsedSources
    : parsedSources.slice(0, 10);
  const hasMoreSources = parsedSources.length > 10;

  return (
    <div className="flex flex-col h-full w-full bg-background z-[9999] border-l border-border">
      {/* Header with Close Button */}
      <div className="flex items-center justify-center p-4 border-b border-border relative z-[10000]">
        {/* Title with source count */}
        <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">{title}</h2>
          {parsedSources.length > 0 && (
            <span className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
              {parsedSources.length}
            </span>
          )}
        </div>

        {/* Close Button - Fixed Right */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute right-4 h-8 w-8 p-0 border-0 bg-transparent hover:bg-transparent"
        >
          <X className="h-5 w-5 text-foreground" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden pl-4 pr-4 pt-4">
        <div className="h-full overflow-y-auto">
            {parsedSources.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center">
                  <Globe className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p>No sources available</p>
                </div>
              </div>
            ) : (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-foreground flex items-center gap-2 mb-2">
                <span>Sources</span>
                <span className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                  {parsedSources.length}
                </span>
              </div>
              <div className="space-y-1.5 max-h-[calc(100vh-200px)] overflow-y-auto bg-sidebar rounded-md p-2 border border-border scrollbar-thin scrollbar-thumb-rounded">
                {visibleSources.map((source, index) => (
                  <a
                    key={`${index}-${source.url || Date.now()}`}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline flex items-start gap-1.5 group min-h-[44px] sm:min-h-0 p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                    <span className="flex-1 break-words line-clamp-2">
                      {source.title || source.url}
                    </span>
                  </a>
                ))}
                {hasMoreSources && !showAllSources && (
                  <button
                    onClick={() => setShowAllSources(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline min-h-[44px] sm:min-h-0 flex items-center w-full justify-center py-2"
                  >
                    Show {parsedSources.length - 10} more sources
                  </button>
                        )}
                      </div>
                    </div>
            )}
        </div>
      </div>
    </div>
  );
});

export default SourcesSidebar;
